import React from 'react';
import { 
    Card, 
    CardActionArea,
    CardContent, 
    Typography, 
    Box, 
    Tooltip,
    useTheme
} from '@mui/material';
import { 
    TrendingUp as TrendingUpIcon, 
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon 
} from '@mui/icons-material';

interface MetricCardProps {
    title: string;
    value: string | number;
    icon?: React.ReactNode;
    trend?: {
        value: number;
        percentage?: number;
        direction: 'up' | 'down' | 'flat';
        isGood?: boolean;
    };
    footer?: string;
    tooltip?: string;
    status?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
    // Optional: makes the card clickable / adds content under the value
    onClick?: () => void;
    children?: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({
    title,
    value,
    icon,
    trend,
    footer,
    tooltip,
    status = 'neutral',
    onClick,
    children
}) => {
    const theme = useTheme();

    // Determine status color
    const getStatusColor = () => {
        switch (status) {
            case 'success':
                return theme.palette.success.main;
            case 'warning':
                return theme.palette.warning.main;
            case 'error':
                return theme.palette.error.main;
            case 'info':
                return theme.palette.primary.main;
            case 'neutral':
            default:
                return theme.palette.text.primary;
        }
    };

    // Determine trend icon and color
    const getTrendIcon = () => {
        const isGood = trend?.isGood !== undefined 
            ? trend.isGood 
            : (trend?.direction === 'up');
            
        const trendColor = isGood 
            ? theme.palette.success.main 
            : theme.palette.error.main;

        switch (trend?.direction) {
            case 'up':
                return <TrendingUpIcon sx={{ color: trendColor, fontSize: 18 }} />;
            case 'down':
                return <TrendingDownIcon sx={{ color: trendColor, fontSize: 18 }} />;
            default:
                return <TrendingFlatIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />;
        }
    };

    const body = (
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {icon && (
                        <Box sx={{ mr: 1, color: theme.palette.primary.main }}>
                            {icon}
                        </Box>
                    )}
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {title}
                    </Typography>
                </Box>

                {/* Value */}
                <Typography 
                    variant="h4" 
                    sx={{ 
                        mb: 1, 
                        color: status !== 'neutral' ? getStatusColor() : 'text.primary',
                        fontWeight: 600
                    }}
                >
                    {value}
                </Typography>

                {/* Trend */}
                {trend && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto', mb: 0.5 }}>
                        {getTrendIcon()}
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {trend.value}{trend.percentage ? ` (${trend.percentage}%)` : ''}
                        </Typography>
                    </Box>
                )}

                {children}

                {/* Footer */}
                {footer && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                        {footer}
                    </Typography>
                )}
            </CardContent>
    );

    const content = (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'visible',
                '&::before': status !== 'neutral' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: getStatusColor(),
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                } : {},
            }}
        >
            {onClick ? (
                <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {body}
                </CardActionArea>
            ) : body}
        </Card>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} arrow placement="top">
                <Box sx={{ height: '100%' }}>{content}</Box>
            </Tooltip>
        );
    }

    return content;
};

export default MetricCard; 